import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, CheckCircle2, Circle, CircleDot, ArrowRight } from "lucide-react";
import memberApi from "../../lib/memberApi";
import { PortalNav } from "../../components/portal/PortalNav";
import { AuraCard } from "../../components/portal/AuraCard";
import { ShareProgress } from "../../components/portal/ShareProgress";

const STATUS = {
  not_started: { label: "Not started", Icon: Circle, cls: "text-white/30" },
  in_progress: { label: "In progress", Icon: CircleDot, cls: "text-accent" },
  complete: { label: "Complete", Icon: CheckCircle2, cls: "text-emerald-400" },
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [n, s] = await Promise.all([
          memberApi.get("/member/nodes"),
          memberApi.get("/portal/settings"),
        ]);
        setData(n.data);
        setSettings(s.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nodes = data?.nodes || [];
  const completeCount = nodes.filter((n) => n.unlocked && n.status === "complete").length;
  const unlockedCount = nodes.filter((n) => n.unlocked).length;
  const pct = unlockedCount ? Math.round((completeCount / unlockedCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay text-white">
      <PortalNav />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="animate-spin text-accent" size={28} />
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-3">// Your mission</p>
              <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-6">The Protocol</h1>

              <div data-testid="progress-summary" className="border border-white/10 bg-[#0a0a0a] p-6 mb-10">
                <div className="flex items-end justify-between mb-3">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                    {completeCount} of {unlockedCount} nodes complete
                  </span>
                  <span className="font-display font-black text-2xl text-accent">{pct}%</span>
                </div>
                <div className="h-2 w-full bg-white/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nodes.map((n, i) => {
                const st = STATUS[n.status] || STATUS.not_started;
                const Body = (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.4) }}
                    className={`group relative h-full border p-6 transition-colors ${
                      n.unlocked
                        ? "border-white/10 bg-[#0a0a0a] hover:border-accent/60"
                        : "border-white/5 bg-[#080808]"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="font-display font-black text-3xl text-white/15 group-hover:text-accent/40 transition-colors">
                        {n.number}
                      </span>
                      {n.unlocked ? (
                        <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${st.cls}`}>
                          <st.Icon size={13} /> {st.label}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">
                          <Lock size={12} /> Locked
                        </span>
                      )}
                    </div>
                    <h3 className={`font-display font-bold text-lg mb-2 ${n.unlocked ? "" : "text-white/40"}`}>{n.title}</h3>
                    <p className="font-body text-sm text-white/45 leading-relaxed mb-4">{n.summary}</p>
                    {n.unlocked ? (
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
                          {n.done_items}/{n.total_items} steps
                        </span>
                        <ArrowRight size={15} className="text-white/40 group-hover:text-accent group-hover:translate-x-1 transition-all" />
                      </div>
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-accent/80">Unlock with the full pack</span>
                    )}
                  </motion.div>
                );
                return n.unlocked ? (
                  <Link key={n.slug} to={`/portal/node/${n.slug}`} data-testid={`node-card-${n.number}`}>{Body}</Link>
                ) : (
                  <a key={n.slug} href="/#buy" data-testid={`node-card-${n.number}`}>{Body}</a>
                );
              })}
            </div>

            {!data?.has_full && (
              <div className="mt-8 border border-accent/40 bg-[#160606] p-6" data-testid="upsell-full-pack">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent mb-2">// Unlock everything</p>
                <p className="font-body text-sm text-white/70 leading-relaxed mb-4">
                  You have the Data Broker Directory. Unlock all 12 nodes plus the bonus chapter with the full pack.
                </p>
                <a href="/#buy" data-testid="upsell-buy-link" className="inline-flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors">
                  Get the full pack <ArrowRight size={14} />
                </a>
              </div>
            )}

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ShareProgress completed={completeCount} total={nodes.length} />
              <AuraCard headline={settings?.aura_headline} subhead={settings?.aura_subhead} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
